// The History tab's small multiples: one SeriesChart per metric, markers shared.
import type { ChartMetric, HistoryVM } from '../../viewModels';
import { stampOfTurn } from '../../viewModels';
import { humanize, oneDecimal, percent, seasonName, whole } from '../format';
import { uiText } from '../text/en';
import { SeriesChart, type Marker } from '../charts/series-chart';

const FORMAT: Record<ChartMetric, (v: number) => string> = {
  population: whole,
  food: whole,
  water: whole,
  wood: whole,
  tools: oneDecimal,
  trust: whole,
  happiness: whole,
  fragility: whole,
  gini: (v) => percent(v),
};

export function Charts({ vm }: { vm: HistoryVM }) {
  const t = uiText.history;
  const markers: Marker[] = vm.markers.map((m) => ({
    index: m.turn - 1,
    kind: m.kind,
    text: t.marker[m.kind](m.count, m.labels.map((l) => (m.kind === 'event' ? humanize(l) : l)).join(', ')),
  }));
  const describe = (i: number): string => {
    const s = stampOfTurn(i + 1);
    return `${seasonName(s.season)}, ${uiText.header.year} ${s.year}`;
  };
  const tick = (i: number): string | null => {
    const s = stampOfTurn(i + 1);
    return s.season === 'spring' && (s.year === 1 || s.year % Math.max(1, Math.ceil(vm.seasons / 40)) === 0)
      ? `Y${s.year}`
      : null;
  };
  return (
    <section aria-labelledby="charts-title">
      <h2 id="charts-title">{t.charts}</h2>
      <p class="hint">{t.markers}</p>
      <div class="history__charts">
        {vm.charts.map((c) => {
          const title = t.metric[c.metric] ?? c.metric;
          const format = FORMAT[c.metric];
          return (
            <SeriesChart
              key={c.metric}
              title={title}
              values={c.values}
              markers={markers}
              format={format}
              tick={tick}
              describe={(i) => `${describe(i)}: ${format(c.values[i] ?? 0)}`}
              summary={t.chartLabel(
                title,
                format(c.values[0] ?? 0),
                format(c.last ?? 0),
                format(c.min),
                format(c.max),
              )}
            />
          );
        })}
      </div>
    </section>
  );
}
